/**
 * <app-smiley-loader> — Foodback morphing smiley loader
 * ============================================================================
 * Standalone Angular component (Angular 17+ — signal inputs, afterNextRender).
 *
 * The face is two eyes and a mouth. It morphs up and down the Foodback
 * sentiment scale (sad → … → big grin) and blooms into the brand smiley,
 * with a little personality on each face: a sigh when sad, a curious
 * look-around on neutral, a double-hop on the grin, a celebratory wiggle on
 * the brand smiley, and the occasional single-eye wink.
 *
 * Everything is computed at runtime from the brand's SVG path data — no
 * images, no animation libraries.
 *
 * USAGE
 *   import { SmileyLoaderComponent } from './smiley-loader.component';
 *
 *   @Component({
 *     imports: [SmileyLoaderComponent],
 *     template: `<app-smiley-loader [size]="96" />`
 *   })
 *
 *   <app-smiley-loader />                            64 px, inherits CSS color
 *   <app-smiley-loader [size]="96" color="#fff" />   for a navy surface
 *   <app-smiley-loader [speed]="0.8" />              calmer pace
 *   Or size with CSS:  app-smiley-loader { width: 120px; height: 120px; }
 *
 * NOTES
 *   - OnPush + the animation loop runs outside NgZone, so 60 fps of DOM
 *     writes never trigger change detection.
 *   - SSR-safe: the template ships a static "gentle smile" first frame;
 *     animation starts via afterNextRender (browser-only).
 *   - Respects prefers-reduced-motion: holds the static smile, no looping.
 *   - File layout: component first, tuning tables in the middle,
 *     the bulky SVG artwork data at the bottom.
 */
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  afterNextRender,
  inject,
  input,
  viewChild,
} from '@angular/core';

// ─────────────────────────────────────────────────────────────────────────────
//  Types
// ─────────────────────────────────────────────────────────────────────────────

type Point = [x: number, y: number];

/** One face of the sentiment scale: two eyes + a mouth, as SVG path data. */
interface FaceArtwork {
  eyeLeft: string;
  eyeRight: string;
  mouth: string;
}

/** One beat of the choreography: morph from one face to the next, then hold. */
interface Beat {
  fromFace: number;
  toFace: number;
  morphSeconds: number;
  holdSeconds: number;
}

/** Where we are inside the choreography on a given frame. */
interface BeatState {
  beat: Beat;
  /** Lower face index of the pair (morphs always run low → high). */
  lowFace: number;
  /** Eased morph position from the low face to the high face, 0..1. */
  direction: number;
  /** Raw morph progress 0..1; exactly 1 means we are holding on a face. */
  morphProgress: number;
  /** Progress through the hold, 0..1 (0 while still morphing). */
  holdProgress: number;
  /** Continuous position on the face scale 0..5 (e.g. 2.5 = between 3 and 4). */
  facePosition: number;
}

/** The whole-face body language for one frame, applied as SVG transforms. */
interface Pose {
  offsetX: number; // head turn, viewBox units
  offsetY: number; // hops (negative) and slumps (positive)
  scale: number; // breathing + excitement
  stretchX: number; // squash & stretch through morphs
  stretchY: number;
  tiltDegrees: number; // gentle sway / look tilt / celebration wiggle
  eyesLookX: number; // eyes leading a head turn (parallax)
  eyelidOpenness: number; // 1 = open, lower = heavy-lidded (the sad sigh)
}

interface EyeShape {
  d: string;
  centerX: number;
  centerY: number;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Component
// ─────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-smiley-loader',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // The hard-coded `d` attributes below are the *gentle smile* (one step above
  // neutral) — the static first frame for SSR and fast page loads, so the
  // loader never greets anyone with a blank space or a sad face.
  template: `
    <svg viewBox="0 0 51.76 47.16" preserveAspectRatio="xMidYMid meet">
      <path #measure class="measure" d="" />
      <g #face [attr.fill]="color()">
        <path
          #mouth
          fill-rule="evenodd"
          d="M9.86,29.46c.83,2.99,2.92,5.87,5.29,7.84,5.44,4.53,13.29,5.65,20.04,3.73,2.1-.6,4.22-1.52,5.88-2.96,1.36-1.18,1.22-3.47,0-4.67-1.35-1.33-3.36-1.18-4.72,0-.36.31.59-.42.44-.33-.22.12-.42.29-.64.42-.31.19-.63.36-.96.52-.1.05-.85.38-.38.18.45-.19-.5.18-.63.22-.59.2-1.19.36-1.8.5-.32.07-.64.13-.96.19,0,0-.55.08-.24.04.31-.04-.56.05-.64.06-.88.07-1.75.09-2.63.06-.38-.01-.75-.03-1.13-.06-.23-.02-.47-.04-.7-.07-.58-.07.43.08-.12-.02-.68-.12-1.35-.26-2.02-.45-.33-.1-.66-.2-.99-.32-.22-.08-.97-.41-.38-.14-.52-.24-1.03-.5-1.53-.79-.25-.15-.5-.3-.74-.47-.2-.14-.9-.76-.28-.19-.71-.66-1.41-1.28-2.06-2.01-.43-.48.14.22.05.07-.11-.18-.25-.34-.37-.51-.22-.31-.42-.64-.61-.97-.17-.3-.34-.61-.49-.92-.07-.14-.12-.29-.2-.42-.08-.14.34.9.17.38-.08-.23-.18-.46-.24-.69-1.14-4.09-7.58-2.35-6.43,1.76" />
        <g #eyes>
          <path
            #eyeLeft
            d="M19.62,8.96c.11,2.15-1.58,3.98-3.76,4.08-2.18.1-4.02-1.56-4.13-3.72-.1-2.15,1.58-3.98,3.76-4.09,2.18-.1,4.02,1.57,4.12,3.72" />
          <path
            #eyeRight
            d="M40.03,9.24c.1,2.15-1.58,3.98-3.75,4.08-2.18.1-4.02-1.56-4.13-3.72-.1-2.16,1.58-3.98,3.76-4.09,2.18-.1,4.03,1.56,4.12,3.72" />
        </g>
      </g>
    </svg>
  `,
  styles: `
    :host {
      display: inline-block;
      width: 64px;
      height: 64px;
      line-height: 0;
      /* on-surface, not primary: the two are the same brand navy on the light theme, but primary
         lightens to a pale lavender in dark and the face washes out against the card. */
      color: var(--mat-sys-on-surface, #1b1d3e);
    }
    svg {
      width: 100%;
      height: 100%;
      display: block;
      overflow: visible;
    }
    .measure {
      visibility: hidden;
    }
  `,
  host: {
    role: 'img',
    'data-testid': 'smiley-loader',
    '[attr.aria-label]': 'ariaLabel()',
    '[style.width.px]': 'size() ?? null',
    '[style.height.px]': 'size() ?? null',
  },
})
export class SmileyLoaderComponent {
  /** Square size in px. Omit to size the host with CSS instead. */
  readonly size = input<number | undefined>(undefined);
  /** Face color. Defaults to currentColor, i.e. the surrounding CSS `color`. */
  readonly color = input('currentColor');
  /** Playback multiplier. Reacts live, without a jump in time. */
  readonly speed = input(1);
  /** Accessible label announced for the loader. Pass a translated string. */
  readonly ariaLabel = input('Loading');

  // Template references
  private readonly measure = viewChild.required<ElementRef<SVGPathElement>>('measure');
  private readonly face = viewChild.required<ElementRef<SVGGElement>>('face');
  private readonly mouth = viewChild.required<ElementRef<SVGPathElement>>('mouth');
  private readonly eyes = viewChild.required<ElementRef<SVGGElement>>('eyes');
  private readonly eyeLeft = viewChild.required<ElementRef<SVGPathElement>>('eyeLeft');
  private readonly eyeRight = viewChild.required<ElementRef<SVGPathElement>>('eyeRight');

  private readonly zone = inject(NgZone);

  // Clock
  private animationFrame = 0;
  private elapsedSeconds = 0; // speed-scaled
  private lastTimestamp = 0;
  private reducedMotionQuery: MediaQueryList | null = null;

  // Geometry, built once on start — see buildGeometry()
  private mouthLoops: Point[][] = [];
  private eyeLeftLoops: Point[][] = [];
  private eyeRightLoops: Point[][] = [];
  private brandMouthOuter: Point[] = [];
  private brandMouthCut: Point[] = [];
  private brandMouthCutOrigin: Point[] = [];

  private readonly lastFace = FACES.length - 1; // index of the brand smiley
  private readonly cycleSeconds = CHOREOGRAPHY.reduce(
    (sum, b) => sum + b.morphSeconds + b.holdSeconds,
    0
  );
  /** Start mid-choreography, at the gentle smile heading upward. */
  private readonly startOffsetSeconds = CHOREOGRAPHY.slice(0, 3).reduce(
    (sum, b) => sum + b.morphSeconds + b.holdSeconds,
    0
  );

  constructor() {
    // afterNextRender runs in the browser only, once the view exists.
    // The server just ships the static smile from the template.
    afterNextRender(() => this.start());
    inject(DestroyRef).onDestroy(() => {
      cancelAnimationFrame(this.animationFrame);
      this.reducedMotionQuery?.removeEventListener('change', this.onReducedMotionChange);
    });
  }

  // ───────────────────────────── Animation loop ─────────────────────────────

  private start(): void {
    this.buildGeometry();
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotionQuery.addEventListener('change', this.onReducedMotionChange);
    if (this.reducedMotionQuery.matches) {
      this.renderFrame(); // hold the static smile — no animation loop
      return;
    }
    this.runLoop();
  }

  /** Start or stop the loop live as the user's motion preference changes. */
  private readonly onReducedMotionChange = (event: MediaQueryListEvent): void => {
    if (event.matches) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = 0;
      this.renderFrame(); // settle on a static frame
    } else if (!this.animationFrame) {
      this.runLoop();
    }
  };

  private runLoop(): void {
    // Outside NgZone: the loop writes straight to the SVG and must not cost
    // a change-detection pass per frame.
    this.zone.runOutsideAngular(() => {
      this.lastTimestamp = performance.now();
      const loop = (now: number) => {
        this.animationFrame = requestAnimationFrame(loop);
        let deltaSeconds = (now - this.lastTimestamp) / 1000;
        this.lastTimestamp = now;
        if (deltaSeconds > 0.25) {
          deltaSeconds = 0.25; // clamp gaps after background-tab throttling
        }
        this.elapsedSeconds += deltaSeconds * this.speed();
        try {
          this.renderFrame();
        } catch {
          /* never let one bad frame kill the loop */
        }
      };
      this.renderFrame(); // paint immediately
      this.animationFrame = requestAnimationFrame(loop);
    });
  }

  private renderFrame(): void {
    const time = this.elapsedSeconds;
    const state = this.locateBeat(time);

    this.renderMouth(state);
    const left = this.renderEye(this.eyeLeft().nativeElement, this.eyeLeftLoops, state);
    const right = this.renderEye(this.eyeRight().nativeElement, this.eyeRightLoops, state);

    const pose = this.computePose(time, state);
    this.applyFaceTransform(pose);
    this.applyEyeTransforms(time, state, left, right, pose);
  }

  /** Walk the choreography table to find the current beat and our progress in it. */
  private locateBeat(time: number): BeatState {
    let local = (time + this.startOffsetSeconds) % this.cycleSeconds;
    let beat = CHOREOGRAPHY[CHOREOGRAPHY.length - 1];
    for (const candidate of CHOREOGRAPHY) {
      const length = candidate.morphSeconds + candidate.holdSeconds;
      if (local < length) {
        beat = candidate;
        break;
      }
      local -= length;
    }
    const morphProgress = local < beat.morphSeconds ? local / beat.morphSeconds : 1;
    const holdProgress =
      local < beat.morphSeconds ? 0 : (local - beat.morphSeconds) / beat.holdSeconds;
    const eased = easeInOutCubic(morphProgress);
    return {
      beat,
      lowFace: Math.min(beat.fromFace, beat.toFace),
      direction: beat.toFace > beat.fromFace ? eased : 1 - eased,
      morphProgress,
      holdProgress,
      facePosition: beat.fromFace + (beat.toFace - beat.fromFace) * eased,
    };
  }

  // ───────────────────────────── Drawing ─────────────────────────────

  private renderMouth(state: BeatState): void {
    const element = this.mouth().nativeElement;
    if (state.lowFace < this.lastFace - 1) {
      // Ordinary beat: one crescent flows into the next.
      element.setAttribute(
        'd',
        morphLoop(
          this.mouthLoops[state.lowFace],
          this.mouthLoops[state.lowFace + 1],
          state.direction
        )
      );
    } else {
      // Grin → brand smiley: the grin ring flows into the outer swoosh while
      // the open-mouth cut grows out of a point. fill-rule="evenodd" carves it.
      const cutGrowth = smoothstep(state.direction, 0.15, 1);
      element.setAttribute(
        'd',
        morphLoop(this.mouthLoops[this.lastFace - 1], this.brandMouthOuter, state.direction) +
          morphLoop(this.brandMouthCutOrigin, this.brandMouthCut, cutGrowth)
      );
    }
  }

  private renderEye(element: SVGPathElement, loops: Point[][], state: BeatState): EyeShape {
    const shape = morphLoopWithCenter(
      loops[state.lowFace],
      loops[state.lowFace + 1],
      state.direction
    );
    element.setAttribute('d', shape.d);
    return shape;
  }

  /** Breathing and sway, squash & stretch, plus each face's character beat. */
  private computePose(time: number, state: BeatState): Pose {
    const pose: Pose = {
      offsetX: 0,
      offsetY: 0,
      scale:
        1 +
        0.02 * Math.sin(time * 2.3) + // breathing
        0.04 * (state.facePosition / this.lastFace), // lift toward happy
      stretchX: 1,
      stretchY: 1,
      tiltDegrees: 2.2 * Math.sin(time * 1.15), // gentle sway
      eyesLookX: 0,
      eyelidOpenness: 1,
    };

    // Squash & stretch through every morph (zero while holding).
    const squash = Math.sin(state.morphProgress * Math.PI);
    pose.stretchY *= 1 + 0.055 * squash;
    pose.stretchX *= 1 - 0.04 * squash;

    // Character beats only play while holding on a face.
    if (state.morphProgress >= 1) {
      switch (state.beat.toFace) {
        case 0:
          this.addSigh(pose, state.holdProgress);
          break;
        case 2:
          this.addLookAround(pose, state);
          break;
        case 4:
          this.addDoubleHop(pose, state.holdProgress);
          break;
        case this.lastFace:
          this.addCelebration(pose, state.holdProgress);
          break;
      }
    }
    return pose;
  }

  /** Sad: breathe in (rise, stretch) … slump down with a squash and heavy lids. */
  private addSigh(pose: Pose, hold: number): void {
    const breatheIn = smoothstep(hold, 0.02, 0.3);
    const slump = smoothstep(hold, 0.34, 0.62);
    const recovery = 1 - smoothstep(hold, 0.82, 1); // pick back up before moving on
    pose.offsetY += (-1.5 * breatheIn + 4.4 * slump) * recovery;
    pose.stretchY *= 1 + (0.05 * breatheIn - 0.085 * slump) * recovery;
    pose.stretchX *= 1 + 0.06 * slump * recovery;
    pose.eyelidOpenness = 1 - 0.32 * slump * recovery;
    pose.tiltDegrees *= 0.35; // too sad to sway much
  }

  /**
   * Neutral: a curious look around. Saccadic like real eyes — dart to one
   * side, hold, dart to the other, hold, return. The whole head turns and the
   * eyes lead it slightly (parallax), so it reads as *looking*, not as the
   * eyes sliding off the face.
   */
  private addLookAround(pose: Pose, state: BeatState): void {
    const hold = state.holdProgress;
    const saccade =
      smoothstep(hold, 0.04, 0.13) -
      2 * smoothstep(hold, 0.44, 0.56) +
      smoothstep(hold, 0.82, 0.92);
    const startRight = state.beat.fromFace < state.beat.toFace; // alternate direction up vs down the scale
    const look = (startRight ? 1 : -1) * saccade;
    pose.offsetX += 1.7 * look; // the head turns…
    pose.eyesLookX = 1.9 * look; // …the eyes lead the turn
    pose.tiltDegrees += 2.4 * look; // tilt toward where it's looking
    pose.offsetY += 0.5 * Math.abs(look); // real head turns arc slightly downward
  }

  /** Big grin: a happy double-hop. */
  private addDoubleHop(pose: Pose, hold: number): void {
    const envelope = Math.sin(hold * Math.PI); // fades the hop in and out of the hold
    pose.offsetY -= 1.7 * Math.abs(Math.sin(hold * Math.PI * 2)) * envelope;
  }

  /** Brand smiley: a joyful wiggle with a proud pop and chin-up lift. */
  private addCelebration(pose: Pose, hold: number): void {
    const easeIn = smoothstep(hold, 0, 0.14); // no velocity kick on landing
    const popEnvelope = smoothstep(hold, 0.02, 0.2) * (1 - smoothstep(hold, 0.6, 1));
    pose.tiltDegrees += 11 * Math.sin(hold * Math.PI * 3) * easeIn * (1 - hold);
    pose.scale += 0.1 * popEnvelope;
    pose.offsetY -= 1.8 * popEnvelope;
  }

  private applyFaceTransform(pose: Pose): void {
    const { x, y } = FACE_CENTER;
    this.face().nativeElement.setAttribute(
      'transform',
      `translate(${pose.offsetX.toFixed(3)} ${pose.offsetY.toFixed(3)}) ` +
        `translate(${x} ${y}) ` +
        `scale(${(pose.scale * pose.stretchX).toFixed(4)} ${(pose.scale * pose.stretchY).toFixed(4)}) ` +
        `rotate(${pose.tiltDegrees.toFixed(3)}) ` +
        `translate(${-x} ${-y})`
    );
  }

  /**
   * Winks and eyelids. Eye closures are always a single-eye wink — never both
   * eyes at once — alternating sides, and only on the happier faces (4+ on
   * the 1–6 scale). The heavy lids of the sad sigh ride on the eye group as a
   * partial droop of both eyes, which is a different thing from a blink.
   */
  private applyEyeTransforms(
    time: number,
    state: BeatState,
    left: EyeShape,
    right: EyeShape,
    pose: Pose
  ): void {
    const winkIndex = Math.floor(time / WINK_EVERY_SECONDS);
    const timeIntoWink = time % WINK_EVERY_SECONDS;
    let openness = 1;
    if (timeIntoWink < WINK_DURATION_SECONDS) {
      openness = 1 - Math.sin((timeIntoWink / WINK_DURATION_SECONDS) * Math.PI) * 0.88;
    }

    const leftEl = this.eyeLeft().nativeElement;
    const rightEl = this.eyeRight().nativeElement;
    leftEl.setAttribute('transform', '');
    rightEl.setAttribute('transform', '');

    const winking = openness < 1 && state.facePosition >= WINK_MINIMUM_FACE;
    if (winking) {
      const useLeftEye = winkIndex % 2 === 0;
      const el = useLeftEye ? leftEl : rightEl;
      const centerY = useLeftEye ? left.centerY : right.centerY;
      el.setAttribute(
        'transform',
        `translate(0 ${(centerY * (1 - openness)).toFixed(3)}) scale(1 ${openness.toFixed(3)})`
      );
    }

    const eyeLineY = (left.centerY + right.centerY) / 2;
    this.eyes().nativeElement.setAttribute(
      'transform',
      `translate(${pose.eyesLookX.toFixed(3)} ${(eyeLineY * (1 - pose.eyelidOpenness)).toFixed(3)}) ` +
        `scale(1 ${pose.eyelidOpenness.toFixed(3)})`
    );
  }

  // ──────────────────────── Geometry preparation ────────────────────────
  //
  // Each face's paths have different point counts, so they can't be morphed
  // directly. We resample every shape into a loop of equally-spaced points
  // (using the browser's own path measurement), then rotationally align each
  // loop with its predecessor so morphs take the shortest way around instead
  // of twisting. Morphing is then just linear interpolation point-by-point.

  private buildGeometry(): void {
    for (let i = 0; i < FACES.length; i++) {
      if (i < this.lastFace) {
        // Faces 0..4 have single-ring crescent mouths that morph into each other.
        const loop = this.sampleLoop(FACES[i].mouth, MOUTH_SAMPLE_COUNT);
        this.mouthLoops[i] = i === 0 ? loop : alignLoops(this.mouthLoops[i - 1], loop);
      }
      const left = this.sampleLoop(FACES[i].eyeLeft, EYE_SAMPLE_COUNT);
      const right = this.sampleLoop(FACES[i].eyeRight, EYE_SAMPLE_COUNT);
      this.eyeLeftLoops[i] = i === 0 ? left : alignLoops(this.eyeLeftLoops[i - 1], left);
      this.eyeRightLoops[i] = i === 0 ? right : alignLoops(this.eyeRightLoops[i - 1], right);
    }

    // The brand smiley's mouth is a compound path: an outer swoosh ring plus
    // an inner ring that cuts the mouth opening. We morph the grin into the
    // outer ring while the cut grows out of its own centroid.
    const rings = FACES[this.lastFace].mouth.split(/(?=M)/g);
    this.brandMouthOuter = alignLoops(
      this.mouthLoops[this.lastFace - 1],
      this.sampleLoop(rings[0], MOUTH_SAMPLE_COUNT)
    );
    this.brandMouthCut = this.sampleLoop(rings[1], MOUTH_SAMPLE_COUNT);
    let centerX = 0,
      centerY = 0;
    for (const point of this.brandMouthCut) {
      centerX += point[0];
      centerY += point[1];
    }
    centerX /= this.brandMouthCut.length;
    centerY /= this.brandMouthCut.length;
    this.brandMouthCutOrigin = this.brandMouthCut.map(() => [centerX, centerY] as Point);
  }

  /** Sample a path into `count` equally-spaced points along its length. */
  private sampleLoop(pathData: string, count: number): Point[] {
    const pathElement = this.measure().nativeElement;
    pathElement.setAttribute('d', pathData);
    const total = pathElement.getTotalLength();
    const points: Point[] = new Array<Point>(count);
    for (let i = 0; i < count; i++) {
      const point = pathElement.getPointAtLength((total * i) / count);
      points[i] = [point.x, point.y];
    }
    return points;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  Tuning
// ─────────────────────────────────────────────────────────────────────────────

const MOUTH_SAMPLE_COUNT = 80;
const EYE_SAMPLE_COUNT = 48;

/** Centre of the artwork's viewBox (0 0 51.76 47.16); all body language pivots here. */
const FACE_CENTER = { x: 25.88, y: 23.58 };

const WINK_EVERY_SECONDS = 2.9;
const WINK_DURATION_SECONDS = 0.18;
/** Winks only from face 4 upward on the 1–6 scale (no winking while glum). */
const WINK_MINIMUM_FACE = 2.95;

/**
 * The choreography: ping-pong up and down the scale. Faces are indices into
 * FACES (0 = saddest … 5 = brand smiley). Each beat morphs, then holds just
 * long enough for that face's character moment.
 */
const CHOREOGRAPHY: Beat[] = [
  { fromFace: 0, toFace: 1, morphSeconds: 0.7, holdSeconds: 0.3 },
  { fromFace: 1, toFace: 2, morphSeconds: 0.7, holdSeconds: 1.0 }, // neutral: look around
  { fromFace: 2, toFace: 3, morphSeconds: 0.7, holdSeconds: 0.3 },
  { fromFace: 3, toFace: 4, morphSeconds: 0.7, holdSeconds: 0.55 }, // grin: double-hop
  { fromFace: 4, toFace: 5, morphSeconds: 0.75, holdSeconds: 1.2 }, // brand smiley: celebration
  { fromFace: 5, toFace: 4, morphSeconds: 0.75, holdSeconds: 0.3 },
  { fromFace: 4, toFace: 3, morphSeconds: 0.65, holdSeconds: 0.25 },
  { fromFace: 3, toFace: 2, morphSeconds: 0.65, holdSeconds: 1.0 }, // neutral: look the other way
  { fromFace: 2, toFace: 1, morphSeconds: 0.65, holdSeconds: 0.25 },
  { fromFace: 1, toFace: 0, morphSeconds: 0.7, holdSeconds: 1.05 }, // sad: a full sigh
];

// ───────────────────────────── Pure helpers ─────────────────────────────

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** Smoothstep of `value` over the window [from, to], clamped to 0..1. */
function smoothstep(value: number, from: number, to: number): number {
  let u = (value - from) / (to - from);
  u = u < 0 ? 0 : u > 1 ? 1 : u;
  return u * u * (3 - 2 * u);
}

/** Linearly interpolate two point loops into an SVG path string. */
function morphLoop(fromLoop: Point[], toLoop: Point[], progress: number): string {
  let d = '';
  for (let i = 0; i < fromLoop.length; i++) {
    const x = fromLoop[i][0] + (toLoop[i][0] - fromLoop[i][0]) * progress;
    const y = fromLoop[i][1] + (toLoop[i][1] - fromLoop[i][1]) * progress;
    d += (i === 0 ? 'M' : 'L') + x.toFixed(2) + ' ' + y.toFixed(2);
  }
  return d + 'Z';
}

/** Same as morphLoop, but also reports the shape's centre (winks pivot on it). */
function morphLoopWithCenter(fromLoop: Point[], toLoop: Point[], progress: number): EyeShape {
  let d = '',
    sumX = 0,
    sumY = 0;
  for (let i = 0; i < fromLoop.length; i++) {
    const x = fromLoop[i][0] + (toLoop[i][0] - fromLoop[i][0]) * progress;
    const y = fromLoop[i][1] + (toLoop[i][1] - fromLoop[i][1]) * progress;
    sumX += x;
    sumY += y;
    d += (i === 0 ? 'M' : 'L') + x.toFixed(2) + ' ' + y.toFixed(2);
  }
  return { d: d + 'Z', centerX: sumX / fromLoop.length, centerY: sumY / fromLoop.length };
}

/**
 * Rotate (and possibly reverse) loop `current` so its points line up with
 * loop `previous` — found by brute force over every rotation of both
 * windings. This is what makes morphs flow instead of twist. Runs once per
 * shape at startup.
 */
function alignLoops(previous: Point[], current: Point[]): Point[] {
  const n = current.length;
  const reversed = current.slice().reverse();
  let best = current;
  let bestError = Infinity;
  for (const candidate of [current, reversed]) {
    for (let offset = 0; offset < n; offset++) {
      let error = 0;
      for (let i = 0; i < n; i++) {
        const a = previous[i];
        const b = candidate[(i + offset) % n];
        const dx = a[0] - b[0];
        const dy = a[1] - b[1];
        error += dx * dx + dy * dy;
        if (error >= bestError) {
          break; // this rotation already lost
        }
      }
      if (error < bestError) {
        bestError = error;
        best = [];
        for (let i = 0; i < n; i++) {
          best.push(candidate[(i + offset) % n]);
        }
      }
    }
  }
  return best;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Artwork — path data extracted from the brand's smiley SVGs.
//  Index 0 is the saddest face, 4 is the big grin, 5 is the brand smiley
//  (whose mouth is a compound path: outer swoosh + the cut that opens it).
// ─────────────────────────────────────────────────────────────────────────────

const FACES: FaceArtwork[] = [
  /* 1 — very dissatisfied: deep frown */
  {
    eyeLeft:
      'M19.6,8.4c.11,2.15-1.58,3.98-3.76,4.08-2.18.1-4.02-1.56-4.13-3.72-.1-2.15,1.58-3.98,3.76-4.09,2.18-.1,4.02,1.57,4.12,3.72',
    eyeRight:
      'M40.01,8.68c.1,2.15-1.58,3.98-3.75,4.08-2.18.1-4.02-1.56-4.13-3.72-.1-2.16,1.58-3.98,3.76-4.09,2.18-.1,4.03,1.56,4.12,3.72',
    mouth:
      'M47.06,39.19c-.1-8.29-6.39-15.14-13.99-17.88-8.04-2.9-17.6-.46-23.31,5.82-3.04,3.33-4.99,7.53-5.05,12.06-.02,1.78,1.55,3.3,3.34,3.3s3.31-1.51,3.34-3.3c0-.35.02-.69.05-1.04,0-.15.03-.3.05-.44-.09.64,0,.11.02,0,.19-.74.37-1.46.62-2.18.05-.14.11-.29.17-.43-.23.53,0,.04.04-.05.17-.32.33-.64.51-.96.18-.32.38-.63.58-.93.09-.14.76-1.01.43-.61.48-.59.99-1.14,1.54-1.66.24-.23.48-.45.73-.66.17-.14.34-.29.51-.42-.28.22,0,0,.12-.08.65-.45,1.31-.87,2.01-1.24.95-.51,1.03-.55,1.87-.84,3.14-1.1,6.14-1.22,9.16-.41,2.86.77,5.22,2.18,7.28,4.35.3.31.58.64.85.97-.3-.36.41.59.51.74.23.35.45.71.65,1.07.08.14.15.27.22.41.03.07.39.8.23.44-.14-.32.06.14.07.17.09.24.18.48.26.72.13.38.23.77.32,1.16.03.15.06.3.1.44.23.89-.02-.36.04.16.05.44.07.89.08,1.34.02,1.78,1.52,3.3,3.34,3.3s3.36-1.51,3.34-3.3',
  },
  /* 2 — dissatisfied: slight frown */
  {
    eyeLeft:
      'M19.62,9.02c.11,2.15-1.58,3.98-3.76,4.08-2.18.1-4.02-1.56-4.13-3.72-.1-2.15,1.58-3.98,3.76-4.09,2.18-.1,4.02,1.57,4.12,3.72',
    eyeRight:
      'M40.03,9.3c.1,2.15-1.58,3.98-3.75,4.08-2.18.1-4.02-1.56-4.13-3.72-.1-2.16,1.58-3.98,3.76-4.09,2.18-.1,4.03,1.56,4.12,3.72',
    mouth:
      'M41.91,37.78c-.83-2.99-2.92-5.87-5.29-7.84-5.44-4.53-13.29-5.65-20.04-3.73-2.1.6-4.22,1.52-5.88,2.96-1.36,1.18-1.22,3.47,0,4.67,1.35,1.33,3.36,1.18,4.72,0,.36-.31-.59.42-.44.33.22-.12.42-.29.64-.42.31-.19.63-.36.96-.52.1-.05.85-.38.38-.18-.45.19.5-.18.63-.22.59-.2,1.19-.36,1.8-.5.32-.07.64-.13.96-.19,0,0,.55-.08.24-.04-.31.04.56-.05.64-.06.88-.07,1.75-.09,2.63-.06.38.01.75.03,1.13.06.23.02.47.04.7.07.58.07-.43-.08.12.02.68.12,1.35.26,2.02.45.33.1.66.2.99.32.22.08.97.41.38.14.52.24,1.03.5,1.53.79.25.15.5.3.74.47.2.14.9.76.28.19.71.66,1.41,1.28,2.06,2.01.43.48-.14-.22-.05-.07.11.18.25.34.37.51.22.31.42.64.61.97.17.3.34.61.49.92.07.14.12.29.2.42.08.14-.34-.9-.17-.38.08.23.18.46.24.69,1.14,4.09,7.58,2.35,6.43-1.76',
  },
  /* 3 — neutral: straight bar mouth */
  {
    eyeLeft:
      'M19.68,12.35c.11,2.15-1.58,3.98-3.76,4.08-2.18.1-4.02-1.56-4.13-3.72-.1-2.15,1.58-3.98,3.76-4.09,2.18-.1,4.02,1.57,4.12,3.72',
    eyeRight:
      'M40.09,12.63c.1,2.15-1.58,3.98-3.75,4.08-2.18.1-4.02-1.56-4.13-3.72-.1-2.16,1.58-3.98,3.76-4.09,2.18-.1,4.03,1.56,4.12,3.72',
    mouth:
      'M11.79,38.54h28.19c1.8,0,3.34-1.52,3.34-3.3s-1.53-3.3-3.34-3.3H11.79c-1.8,0-3.34,1.52-3.34,3.3s1.53,3.3,3.34,3.3',
  },
  /* 4 — satisfied: gentle smile (also the loader's static first frame) */
  {
    eyeLeft:
      'M19.62,8.96c.11,2.15-1.58,3.98-3.76,4.08-2.18.1-4.02-1.56-4.13-3.72-.1-2.15,1.58-3.98,3.76-4.09,2.18-.1,4.02,1.57,4.12,3.72',
    eyeRight:
      'M40.03,9.24c.1,2.15-1.58,3.98-3.75,4.08-2.18.1-4.02-1.56-4.13-3.72-.1-2.16,1.58-3.98,3.76-4.09,2.18-.1,4.03,1.56,4.12,3.72',
    mouth:
      'M9.86,29.46c.83,2.99,2.92,5.87,5.29,7.84,5.44,4.53,13.29,5.65,20.04,3.73,2.1-.6,4.22-1.52,5.88-2.96,1.36-1.18,1.22-3.47,0-4.67-1.35-1.33-3.36-1.18-4.72,0-.36.31.59-.42.44-.33-.22.12-.42.29-.64.42-.31.19-.63.36-.96.52-.1.05-.85.38-.38.18.45-.19-.5.18-.63.22-.59.2-1.19.36-1.8.5-.32.07-.64.13-.96.19,0,0-.55.08-.24.04.31-.04-.56.05-.64.06-.88.07-1.75.09-2.63.06-.38-.01-.75-.03-1.13-.06-.23-.02-.47-.04-.7-.07-.58-.07.43.08-.12-.02-.68-.12-1.35-.26-2.02-.45-.33-.1-.66-.2-.99-.32-.22-.08-.97-.41-.38-.14-.52-.24-1.03-.5-1.53-.79-.25-.15-.5-.3-.74-.47-.2-.14-.9-.76-.28-.19-.71-.66-1.41-1.28-2.06-2.01-.43-.48.14.22.05.07-.11-.18-.25-.34-.37-.51-.22-.31-.42-.64-.61-.97-.17-.3-.34-.61-.49-.92-.07-.14-.12-.29-.2-.42-.08-.14.34.9.17.38-.08-.23-.18-.46-.24-.69-1.14-4.09-7.58-2.35-6.43,1.76',
  },
  /* 5 — very satisfied: big grin */
  {
    eyeLeft:
      'M19.6,8.4c.11,2.15-1.58,3.98-3.76,4.08-2.18.1-4.02-1.56-4.13-3.72-.1-2.15,1.58-3.98,3.76-4.09,2.18-.1,4.02,1.57,4.12,3.72',
    eyeRight:
      'M40.01,8.69c.1,2.15-1.58,3.98-3.75,4.08-2.18.1-4.02-1.56-4.13-3.72-.1-2.16,1.58-3.98,3.76-4.09,2.18-.1,4.03,1.56,4.12,3.72',
    mouth:
      'M40.39,23.39c0,.35-.02.69-.05,1.04,0,.15-.03.3-.05.44.09-.64,0-.11-.02,0-.19.74-.37,1.46-.62,2.18-.05.14-.11.29-.17.43.23-.53,0-.04-.04.05-.17.32-.33.64-.51.96-.18.32-.38.63-.58.93-.09.14-.76,1.01-.43.61-.48.59-.99,1.14-1.54,1.66-.24.23-.48.45-.73.66-.17.14-.34.29-.51.42.28-.22,0,0-.12.08-.65.45-1.31.87-2.01,1.24-.95.51-1.03.55-1.87.84-3.14,1.1-6.14,1.22-9.16.41-2.86-.77-5.22-2.18-7.28-4.35-.3-.31-.58-.64-.85-.97.3.36-.41-.59-.51-.74-.23-.35-.45-.71-.65-1.07-.08-.14-.15-.27-.22-.41-.03-.07-.39-.8-.23-.44.14.32-.06-.14-.07-.17-.09-.24-.18-.48-.26-.72-.13-.38-.23-.77-.32-1.16-.03-.15-.06-.3-.1-.44-.23-.89.02.36-.04-.16-.05-.44-.07-.89-.08-1.34-.02-1.78-1.52-3.3-3.34-3.3s-3.36,1.51-3.34,3.3c.1,8.29,6.39,15.14,13.99,17.88,8.04,2.9,17.6.46,23.31-5.82,3.04-3.33,4.99-7.53,5.05-12.06.02-1.78-1.55-3.3-3.34-3.3s-3.31,1.51-3.34,3.3',
  },
  /* 6 — the Foodback brand smiley: tilted eyes, swoosh mouth (compound path) */
  {
    eyeLeft:
      'M25.15,8.24c-.73,2.07-3.01,3.15-5.08,2.42-2.07-.74-3.15-3.01-2.41-5.08.73-2.07,3.01-3.15,5.08-2.42,2.07.74,3.14,3.01,2.41,5.09',
    eyeRight:
      'M44.07,16.31c-.73,2.07-3.01,3.15-5.08,2.42-2.07-.74-3.15-3.01-2.41-5.09.74-2.07,3.01-3.15,5.08-2.42,2.07.74,3.15,3.01,2.41,5.09',
    mouth:
      'M44.52,25.09c-1.44-.59-3.09-.06-3.98,1.14-8.67-3.56-17.35-7.11-26.02-10.67-.41-.17-.82-.33-1.22-.5.2-1.48-.6-3.02-2.04-3.61-1.67-.69-3.71.14-4.38,1.84-.33.83-.61,1.68-.84,2.54-.08.22-.14.44-.17.66-1.84,7.7.12,16.37,5.74,22.05,3.17,3.21,7.42,5.3,11.95,5.59,4.64.3,9.08-1.01,13.04-3.37,4.34-2.59,7.81-6.62,9.76-11.28.7-1.68-.17-3.7-1.84-4.39M32.95,35.15s-.17.1-.25.15c-.16.09-.31.17-.47.25-.79.41-1.61.77-2.45,1.07-.21.08-.42.14-.63.21-.04.01-.22.07-.29.09-.32.08-.64.15-.96.22-.87.17-1.75.28-2.63.32.13-.02-.81-.02-.91-.02-.38-.02-.76-.06-1.14-.12-.44-.06-.87-.14-1.3-.24-.02,0-.03,0-.04,0-.02,0-.03,0-.05-.01-.27-.08-.53-.17-.79-.26-.58-.21-1.57-.66-1.94-.85-.05-.04-.36-.22-.41-.26-.18-.12-.37-.24-.54-.37-.4-.29-.79-.59-1.16-.91-.17-.14-.33-.29-.49-.44-.03-.02-.1-.1-.16-.16-.23-.25-.46-.51-.68-.77-.56-.68-1.08-1.41-1.53-2.17,0,0-.31-.6-.36-.7-.2-.4-.38-.81-.55-1.22-.15-.36-.29-.73-.41-1.1-.07-.21-.14-.43-.21-.64-.03-.1-.06-.17-.08-.23-.01-.06-.03-.13-.05-.25-.22-.99-.41-1.99-.5-3.01-.1-1.05-.12-1.2-.08-1.99,8.29,3.4,16.58,6.8,24.86,10.2.03.01.06.03.09.04-.82.87-1.75,1.68-2.77,2.41-.37.26-.75.51-1.12.75',
  },
];
